import { margem, vigente, saldoExibido, totalVigentes } from '../saldo-operacoes';
import { SaldosBancarios } from './SaldosBancarios';
import { resumoVincendas } from '../resumo-vincendas';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Landmark,
  Sprout,
  Wallet,
  ShieldCheck,
  CalendarDays,
  AlertCircle,
  CheckCircle2,
  Clock,
  FileText,
  Download,
  Warehouse,
  Percent,
  SlidersHorizontal,
} from 'lucide-react';
import { Chip, Modal, Select, Stat, IconBox } from '@/ui/primitives';
import { GridAuto } from '@/ui/layout';
import { cn } from '@/lib/utils';
import { formatDateBR, formatBRL } from '@/lib/format';
import { useOperacoesCredito, useOperacaoDetalhe } from '../operacoes-api';
import type { Operacao, Parcela, Liberacao, SeguroConformidade } from '../operacoes-schemas';
import { InstituicaoLogo } from './InstituicaoLogo';

// ── helpers ───────────────────────────────────────────────────────────────────

function statusTone(status: string): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'ativa') return 'ok';
  if (status === 'inadimplente') return 'danger';
  if (status === 'prorrogada') return 'warn';
  if (status === 'quitada') return 'neutral';
  return 'neutral';
}

function parcelaTone(status: Parcela['status']): 'ok' | 'warn' | 'danger' | 'neutral' {
  if (status === 'paga') return 'ok';
  if (status === 'atrasada') return 'danger';
  if (status === 'pendente') return 'warn';
  return 'neutral';
}

function liberacaoTone(status: Liberacao['status']): 'ok' | 'warn' | 'info' | 'neutral' {
  if (status === 'liberada') return 'ok';
  if (status === 'cancelada') return 'warn';
  if (status === 'prevista') return 'info';
  return 'neutral';
}

function garantiaTipo(tipo: string, grau?: number | null): string {
  const map: Record<string, string> = {
    penhor_cedular: 'Penhor cedular',
    alienacao_fiduciaria: 'Alien. fiduciária',
    hipoteca: 'Hipoteca',
    aval: 'Aval',
    outros: 'Outros',
  };
  const base = map[tipo] ?? tipo;
  if (tipo === 'hipoteca' && grau != null) {
    const ordinal = `${grau}º`;
    return `Hipoteca ${ordinal} grau`;
  }
  return base;
}

// Hipoteca com grau ≥ 5 = exposure elevado, vira warn; ≥ 8 = danger
function hipotecaTone(grau: number | null | undefined): 'info' | 'warn' | 'danger' {
  if (grau == null) return 'info';
  if (grau >= 8) return 'danger';
  if (grau >= 5) return 'warn';
  return 'info';
}

function participantePapelLabel(p: string): string {
  const map: Record<string, string> = {
    emitente_principal: 'Emitente principal',
    emitente_secundario: 'Co-emitente',
    outorga_conjugal: 'Outorga conjugal',
    avalista: 'Avalista',
    fiel_depositario: 'Fiel depositário',
    terceiro_interveniente: 'Terceiro interveniente',
    terceiro_anuente: 'Terceiro anuente',
  };
  return map[p] ?? p;
}

function participanteTone(p: string): 'info' | 'accent' | 'ok' | 'warn' | 'danger' | 'neutral' {
  if (p === 'emitente_principal') return 'info';
  if (p === 'emitente_secundario') return 'accent';
  if (p === 'outorga_conjugal') return 'accent';
  if (p === 'avalista') return 'warn';
  if (p === 'fiel_depositario') return 'ok';
  if (p === 'terceiro_interveniente') return 'danger';
  return 'neutral';
}

function shortTipoInstrumento(t: string): string {
  if (!t) return '';
  if (t === 'CCB') return 'CCB';
  if (t.toLowerCase().includes('pignoratícia')) return 'CRPH';
  if (t === 'CPR') return 'CPR';
  return t;
}

function seguroTipoLabel(tipo: string): string {
  const map: Record<string, string> = {
    vida_prestamista: 'Vida prestamista',
    penhor_rural: 'Penhor rural',
    proagro: 'Proagro',
    agricola: 'Agrícola',
    patrimonial: 'Patrimonial',
    outros: 'Outros',
  };
  return map[tipo] ?? tipo;
}

function anexoCategoriaLabel(c: string): string {
  const map: Record<string, string> = {
    cedula: 'Cédula',
    cet: 'CET',
    orcamento: 'Orçamento',
    ovpr: 'OVPR / Adesão',
    aditivo: 'Aditivo',
    recibo_deposito: 'Recibo de depósito',
    apolice_seguro: 'Apólice de seguro',
    comprovante_pagamento: 'Comprovante de pagamento',
    outros: 'Outros',
  };
  return map[c] ?? c;
}

function anexoTone(c: string): 'info' | 'accent' | 'ok' | 'warn' | 'neutral' {
  if (c === 'cedula' || c === 'aditivo') return 'info';
  if (c === 'apolice_seguro') return 'accent';
  if (c === 'recibo_deposito' || c === 'comprovante_pagamento') return 'ok';
  if (c === 'cet' || c === 'orcamento' || c === 'ovpr') return 'warn';
  return 'neutral';
}

function grupoOrcamentoTone(
  g: string | null | undefined,
): 'info' | 'accent' | 'ok' | 'warn' | 'danger' | 'neutral' {
  if (!g) return 'neutral';
  const up = g.toUpperCase();
  if (up.startsWith('PREPARO DO SOLO')) return 'info';
  if (up.startsWith('TRATOS CULTURAIS')) return 'ok';
  if (up.startsWith('COLHEITA')) return 'warn';
  if (up.startsWith('INVESTIMENTO')) return 'accent';
  if (
    up.startsWith('RECRIA') ||
    up.startsWith('ENGORDA') ||
    up.includes('PECUÁRIA') ||
    up.includes('PECUARIA')
  )
    return 'accent';
  if (up.startsWith('SEGURO')) return 'danger';
  return 'neutral';
}

function formatBytes(b: number | null | undefined): string {
  if (b == null) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// Extrai linhas marcadas como "Pendência:" no campo observacoes.
// Aceita "Pendência:" no início de linha ou após newline, case-insensitive.
function extrairPendencias(obs: string | null | undefined): string[] {
  if (!obs) return [];
  const lines = obs.split(/\r?\n/);
  return lines
    .map((l) => l.trim())
    .filter((l) => /^pend[êe]ncia\s*:/i.test(l))
    .map((l) => l.replace(/^pend[êe]ncia\s*:\s*/i, ''));
}

function getSafra(op: Operacao): string | null {
  const baseDate = op.data_contratacao ?? op.periodo_agricola_ini;
  if (!baseDate) return null;
  const d = new Date(baseDate);
  if (Number.isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const cultura = op.cultura?.toLowerCase() ?? '';

  if (cultura.includes('soja')) return `Safra ${year}/${year + 1}`;
  if (cultura.includes('milho')) {
    if (op.modalidade === 'renegociacao' && op.periodo_agricola_ini) {
      return `Safra ${new Date(op.periodo_agricola_ini).getFullYear()}`;
    }
    return `Safra ${year + 1}`;
  }

  if (op.periodo_agricola_ini) {
    const periodo = new Date(op.periodo_agricola_ini);
    if (!Number.isNaN(periodo.getTime())) return `Safra ${periodo.getFullYear()}`;
  }
  return null;
}

function seguroTone(tipo: string): 'info' | 'accent' | 'ok' | 'warn' | 'neutral' {
  if (tipo === 'vida_prestamista') return 'info';
  if (tipo === 'penhor_rural') return 'accent';
  if (tipo === 'proagro') return 'ok';
  if (tipo === 'agricola') return 'warn';
  return 'neutral';
}

function seguroConformidadeTone(status: SeguroConformidade['status']): 'ok' | 'warn' | 'danger' {
  if (status === 'ok') return 'ok';
  if (status === 'warn') return 'warn';
  return 'danger';
}

function seguroConformidadeIcon(status: SeguroConformidade['status']) {
  if (status === 'ok') return CheckCircle2;
  return AlertCircle;
}

function seguroConformidadeBorder(status: SeguroConformidade['status']): string {
  if (status === 'ok') return 'rgba(22,163,74,0.3)';
  if (status === 'warn') return 'rgba(217,119,6,0.34)';
  return 'rgba(220,38,38,0.34)';
}

function seguroConformidadeBg(status: SeguroConformidade['status']): string {
  if (status === 'ok') return 'rgba(22,163,74,0.06)';
  if (status === 'warn') return 'rgba(217,119,6,0.07)';
  return 'rgba(220,38,38,0.07)';
}

function garantiaTone(tipo: string): 'info' | 'accent' | 'warn' | 'ok' | 'neutral' {
  if (tipo === 'penhor_cedular') return 'info';
  if (tipo === 'alienacao_fiduciaria') return 'accent';
  if (tipo === 'hipoteca') return 'warn';
  if (tipo === 'aval') return 'ok';
  return 'neutral';
}

function modalidadeLabel(m: string): string {
  // custeio_agricola / custeio_pecuario reusam a etiqueta "Custeio" — o setor
  // (agrícola/pecuário) já aparece em outro lugar, evita redundância.
  if (m.startsWith('custeio')) return 'Custeio';
  if (m === 'investimento') return 'Investimento';
  if (m === 'comercializacao') return 'Comercialização';
  return m;
}

function modalidadeTone(m: string): 'info' | 'accent' | 'ok' | 'neutral' {
  if (m.startsWith('custeio')) return 'info';
  if (m === 'investimento') return 'accent';
  if (m === 'comercializacao') return 'ok';
  return 'neutral';
}

function formatTaxa(taxa: number | null | undefined): string {
  if (taxa == null) return '—';
  const pct = taxa < 1 ? taxa * 100 : taxa;
  return `${pct.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}% a.a.`;
}

// Versão compacta pra stat cards: usa notation 'compact' quando >= 1 milhão
// (R$ 1,15 mi) e mantém formato completo abaixo disso (R$ 850.000,00).
function formatBRLCompact(value: number): string {
  if (Math.abs(value) >= 1_000_000) {
    return value.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      notation: 'compact',
      maximumFractionDigits: 2,
    });
  }
  return formatBRL(value);
}

// ── sub-componentes ───────────────────────────────────────────────────────────

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <p
      className="text-text-muted uppercase font-semibold"
      style={{ fontSize: '0.66rem', letterSpacing: '0.07em' }}
    >
      {children}
    </p>
  );
}

// A classe `.empty-state` é do DS legado (v7) e não existe no CSS do v9 — por isso
// o estado vazio é montado com utilitários aqui.
function EmptyBlock({
  icon,
  title,
  desc,
}: {
  icon: React.ReactNode;
  title: string;
  desc?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 py-14 text-center">
      <span className="text-text-muted">{icon}</span>
      <p className="text-sm font-medium text-text">{title}</p>
      {desc && <p className="text-[0.82rem] text-text-muted max-w-[380px]">{desc}</p>}
    </div>
  );
}

function RequiredSectionEmpty({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="text-text-muted"
      style={{
        padding: '0.7rem 0.85rem',
        border: '1px dashed var(--border)',
        borderRadius: 'var(--radius-md)',
        fontSize: '0.82rem',
        lineHeight: 1.45,
      }}
    >
      {children}
    </div>
  );
}

function DetalheContent({ id }: { id: number }) {
  const { data, isLoading } = useOperacaoDetalhe(id);

  if (isLoading) {
    return (
      <div className="module-loading" style={{ padding: '1rem' }}>
        Carregando detalhe…
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="flex flex-col gap-8">
      {/* Pendências (extraídas de observacoes) */}
      {(() => {
        const pendencias = extrairPendencias(data.observacoes);
        if (pendencias.length === 0) return null;
        return (
          <div
            className="flex flex-col gap-2 p-4"
            style={{
              background: 'rgba(217,119,6,0.06)',
              border: '1px solid rgba(217,119,6,0.25)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <div className="flex items-center gap-2">
              <AlertCircle
                size={16}
                strokeWidth={1.5}
                style={{ color: 'var(--warning, #d97706)' }}
              />
              <span
                className="uppercase font-semibold"
                style={{
                  fontSize: '0.7rem',
                  letterSpacing: '0.06em',
                  color: 'var(--warning, #d97706)',
                }}
              >
                Pendências ({pendencias.length})
              </span>
            </div>
            <ul
              className="flex flex-col gap-1.5"
              style={{ paddingLeft: '1.4rem', listStyleType: 'disc' }}
            >
              {pendencias.map((p, i) => (
                <li
                  key={i}
                  className="text-text-2"
                  style={{ fontSize: '0.82rem', lineHeight: 1.45 }}
                >
                  {p}
                </li>
              ))}
            </ul>
          </div>
        );
      })()}

      {/* Encargos */}
      <div className="flex flex-col gap-3">
        <SectionLabel>Encargos</SectionLabel>
        <div className="flex flex-wrap gap-2">
          {data.taxa_juros_aa != null && (
            <Chip tone="info" size="sm">
              {formatTaxa(data.taxa_juros_aa)}
              {data.taxa_juros_aa_sem_bonus != null && ' c/bônus'}
            </Chip>
          )}
          {data.taxa_juros_aa_sem_bonus != null && (
            <Chip tone="neutral" size="sm">
              {formatTaxa(data.taxa_juros_aa_sem_bonus)} s/bônus
            </Chip>
          )}
          {data.recurso && (
            <Chip tone="accent" size="sm">
              Recurso {data.recurso}
            </Chip>
          )}
          {data.setor && (
            <Chip tone="ok" size="sm">
              {data.setor === 'pecuario'
                ? 'Pecuário'
                : data.setor === 'agricola'
                  ? 'Agrícola'
                  : data.setor}
              {data.subfinalidade && ` · ${data.subfinalidade}`}
            </Chip>
          )}
          <Chip tone="warn" size="sm">
            Mora 1%/mês
          </Chip>
          <Chip tone="danger" size="sm">
            Multa 2%
          </Chip>
          {data.adesao_proagro && (
            <Chip tone="ok" size="sm">
              Proagro
            </Chip>
          )}
          {data.ref_bacen && (
            <Chip tone="accent" size="sm">
              SICOR {data.ref_bacen}
            </Chip>
          )}
          {data.tarifa_estudo != null && (
            <Chip tone="accent" size="sm">
              Tarifa estudo {formatBRL(data.tarifa_estudo)}
            </Chip>
          )}
        </div>
      </div>

      {/* Participantes */}
      <div className="flex flex-col gap-3">
        <SectionLabel>Participantes</SectionLabel>
        {data.participantes && data.participantes.length > 0 ? (
          <div className="flex flex-col gap-2">
            {data.participantes.map((p) => (
              <div
                key={p.id}
                className="flex items-center gap-3 flex-wrap"
                style={{
                  padding: '0.6rem 0.8rem',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                }}
              >
                <Chip tone={participanteTone(p.papel)} size="sm">
                  {participantePapelLabel(p.papel)}
                </Chip>
                <span className="font-medium" style={{ fontSize: '0.85rem' }}>
                  {p.nome ?? p.cliente_nome_link ?? '—'}
                </span>
                {p.cpf && (
                  <span
                    className="text-text-muted"
                    style={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {p.cpf.replace(/\D/g, '').length === 14 ? 'CNPJ' : 'CPF'} {p.cpf}
                  </span>
                )}
              </div>
            ))}
          </div>
        ) : (
          <RequiredSectionEmpty>
            Pendente de cadastro: nenhum participante vinculado à operação.
          </RequiredSectionEmpty>
        )}
      </div>

      {/* Liberações */}
      <div className="flex flex-col gap-3">
        <SectionLabel>Liberações</SectionLabel>
        {data.liberacoes && data.liberacoes.length > 0 ? (
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Data prevista</th>
                <th>Data efetivada</th>
                <th style={{ textAlign: 'right' }}>Valor</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.liberacoes.map((lib) => (
                <tr key={lib.id}>
                  <td style={{ fontSize: '0.82rem' }}>
                    {lib.data_prevista
                      ? formatDateBR(lib.data_prevista)
                      : lib.referencia_textual === 'IMEDIATA'
                        ? `Imediata${lib.previsao_operacional_dias ? ` (estimativa operacional: ${lib.previsao_operacional_dias} dias)` : ''}`
                        : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {lib.data_efetivada ? formatDateBR(lib.data_efetivada) : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'right' }}>
                    {formatBRL(lib.valor)}
                  </td>
                  <td>
                    <Chip tone={liberacaoTone(lib.status)} size="sm">
                      {lib.status === 'liberada'
                        ? 'Liberada'
                        : lib.status === 'cancelada'
                          ? 'Cancelada'
                          : 'Prevista'}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <RequiredSectionEmpty>
            Pendente de cadastro: cronograma de liberação não informado.
          </RequiredSectionEmpty>
        )}
      </div>

      {/* Parcelas */}
      {data.parcelas && data.parcelas.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionLabel>Parcelamento</SectionLabel>
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th style={{ width: 36 }}>#</th>
                <th>Vencimento</th>
                <th style={{ textAlign: 'right' }}>Valor previsto</th>
                <th style={{ textAlign: 'right' }}>Valor pago</th>
                <th>Pagamento</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {data.parcelas.map((p) => (
                <tr key={p.id}>
                  <td style={{ fontSize: '0.82rem', color: 'var(--text-muted)' }}>{p.numero}</td>
                  <td style={{ fontSize: '0.82rem' }}>{formatDateBR(p.data_vencimento)}</td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'right' }}>
                    {p.valor_previsto != null
                      ? formatBRL(p.valor_previsto)
                      : p.percentual_saldo_devedor != null
                        ? `${p.percentual_saldo_devedor.toLocaleString('pt-BR')}% do saldo devedor`
                        : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'right' }}>
                    {p.valor_pago != null ? formatBRL(p.valor_pago) : '—'}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {p.data_pagamento ? formatDateBR(p.data_pagamento) : '—'}
                  </td>
                  <td>
                    <Chip tone={parcelaTone(p.status)} size="sm">
                      {p.status === 'paga'
                        ? 'Paga'
                        : p.status === 'atrasada'
                          ? 'Atrasada'
                          : 'Pendente'}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Conformidade dos seguros */}
      {data.conformidade_seguros && data.conformidade_seguros.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionLabel>Conformidade dos seguros</SectionLabel>
          <div className="grid gap-2">
            {data.conformidade_seguros.map((item) => {
              const Icon = seguroConformidadeIcon(item.status);
              return (
                <div
                  key={item.seguro_id}
                  className="flex flex-col gap-2"
                  style={{
                    padding: '0.75rem 0.85rem',
                    border: `1px solid ${seguroConformidadeBorder(item.status)}`,
                    borderRadius: 'var(--radius-md)',
                    background: seguroConformidadeBg(item.status),
                  }}
                >
                  <div className="flex items-center gap-2 flex-wrap">
                    <Icon
                      size={16}
                      strokeWidth={1.6}
                      style={{
                        color:
                          item.status === 'ok'
                            ? 'var(--success, #16a34a)'
                            : item.status === 'warn'
                              ? 'var(--warning, #d97706)'
                              : 'var(--danger, #dc2626)',
                      }}
                    />
                    <Chip tone={seguroTone(item.tipo)} size="sm">
                      {seguroTipoLabel(item.tipo)}
                    </Chip>
                    <Chip tone={seguroConformidadeTone(item.status)} size="sm">
                      {item.label}
                    </Chip>
                    <span
                      className="text-text-muted"
                      style={{ fontSize: '0.78rem', fontVariantNumeric: 'tabular-nums' }}
                    >
                      Premio {formatBRL(item.premio)}
                    </span>
                  </div>
                  <div className="text-text-2" style={{ fontSize: '0.82rem', lineHeight: 1.45 }}>
                    {item.resumo}
                  </div>
                  {item.pendencias.length > 0 && (
                    <ul
                      className="flex flex-col gap-1"
                      style={{ paddingLeft: '1.15rem', listStyleType: 'disc' }}
                    >
                      {item.pendencias.map((p, i) => (
                        <li
                          key={i}
                          className="text-text-2"
                          style={{ fontSize: '0.78rem', lineHeight: 1.4 }}
                        >
                          {p}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="text-text-muted" style={{ fontSize: '0.76rem', lineHeight: 1.4 }}>
                    {item.recomendacao}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Seguros */}
      <div className="flex flex-col gap-3">
        <SectionLabel>Seguros contratados</SectionLabel>
        {data.seguros && data.seguros.length > 0 ? (
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Seguradora</th>
                <th style={{ textAlign: 'right' }}>Capital segurado</th>
                <th style={{ textAlign: 'right' }}>Prêmio</th>
                <th>Vigência</th>
              </tr>
            </thead>
            <tbody>
              {data.seguros.map((s) => (
                <tr key={s.id}>
                  <td>
                    <Chip tone={seguroTone(s.tipo)} size="sm">
                      {seguroTipoLabel(s.tipo)}
                    </Chip>
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    <span className="font-medium">{s.produto ?? '—'}</span>
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {s.capital_segurado != null ? formatBRL(s.capital_segurado) : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatBRL(s.premio)}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    {s.vigencia_fim ? formatDateBR(s.vigencia_fim) : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <RequiredSectionEmpty>
            Sem seguro cadastrado para esta operação. Confirmar nos documentos se não se aplica.
          </RequiredSectionEmpty>
        )}
      </div>

      {/* Orçamento de Aplicação */}
      {data.orcamento_itens && data.orcamento_itens.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionLabel>Orçamento de Aplicação</SectionLabel>
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Grupo</th>
                <th>Discriminação</th>
                <th style={{ textAlign: 'center' }}>Und</th>
                <th style={{ textAlign: 'right' }}>Qtde</th>
                <th style={{ textAlign: 'right' }}>Vr. unitário</th>
                <th style={{ textAlign: 'right' }}>Rec. próprios</th>
                <th style={{ textAlign: 'right' }}>Financiado</th>
                <th style={{ textAlign: 'right' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Época</th>
              </tr>
            </thead>
            <tbody>
              {data.orcamento_itens.map((it) => (
                <tr key={it.id}>
                  <td style={{ fontSize: '0.76rem' }}>
                    {it.grupo && (
                      <Chip tone={grupoOrcamentoTone(it.grupo)} size="sm">
                        {it.grupo}
                      </Chip>
                    )}
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>{it.discriminacao}</td>
                  <td
                    style={{ fontSize: '0.78rem', textAlign: 'center' }}
                    className="text-text-muted"
                  >
                    {it.unidade ?? '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {it.quantidade != null
                      ? it.quantidade.toLocaleString('pt-BR', { maximumFractionDigits: 4 })
                      : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {it.valor_unitario != null ? formatBRL(it.valor_unitario) : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.78rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                    className="text-text-muted"
                  >
                    {it.recursos_proprios != null && it.recursos_proprios > 0
                      ? formatBRL(it.recursos_proprios)
                      : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {it.valor_financiado != null ? formatBRL(it.valor_financiado) : '—'}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'right',
                      fontVariantNumeric: 'tabular-nums',
                      fontWeight: 600,
                    }}
                  >
                    {it.valor_total != null ? formatBRL(it.valor_total) : '—'}
                  </td>
                  <td
                    style={{ fontSize: '0.78rem', textAlign: 'center' }}
                    className="text-text-muted"
                  >
                    {it.epoca ?? '—'}
                  </td>
                </tr>
              ))}
              <tr style={{ borderTop: '2px solid var(--border)', fontWeight: 600 }}>
                <td colSpan={5} style={{ fontSize: '0.82rem', textAlign: 'right' }}>
                  TOTAL
                </td>
                <td
                  style={{
                    fontSize: '0.82rem',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatBRL(
                    data.orcamento_itens.reduce((s, i) => s + (i.recursos_proprios ?? 0), 0),
                  )}
                </td>
                <td
                  style={{
                    fontSize: '0.82rem',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatBRL(
                    data.orcamento_itens.reduce((s, i) => s + (i.valor_financiado ?? 0), 0),
                  )}
                </td>
                <td
                  style={{
                    fontSize: '0.82rem',
                    textAlign: 'right',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatBRL(data.orcamento_itens.reduce((s, i) => s + (i.valor_total ?? 0), 0))}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </div>
      )}

      {/* Garantias */}
      <div className="flex flex-col gap-3">
        <SectionLabel>Garantias</SectionLabel>
        {data.garantias && data.garantias.length > 0 ? (
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Tipo</th>
                <th>Cultura / Imóvel</th>
                <th style={{ textAlign: 'center' }}>Matrícula</th>
                <th style={{ textAlign: 'center' }}>Quantidade</th>
                <th style={{ textAlign: 'right' }}>Valor SC</th>
                <th style={{ textAlign: 'right' }}>Valor total</th>
              </tr>
            </thead>
            <tbody>
              {data.garantias.map((g) => {
                const isPenhor = g.tipo === 'penhor_cedular' || g.tipo === 'penhor_rural';
                const isHipoteca = g.tipo === 'hipoteca';
                const isAnimal = isPenhor && g.quantidade_cabecas != null;
                const valorTotal = g.valor_estimado ?? g.valor_avaliacao;
                const tipoTone = isHipoteca ? hipotecaTone(g.grau_hipoteca) : garantiaTone(g.tipo);
                return (
                  <tr key={g.id}>
                    <td>
                      <Chip tone={tipoTone} size="sm">
                        {garantiaTipo(g.tipo, g.grau_hipoteca)}
                      </Chip>
                    </td>
                    <td style={{ fontSize: '0.82rem' }}>
                      {isPenhor && g.produto ? (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium">{g.produto}</span>
                          {isAnimal && g.categoria_animal && (
                            <span className="text-text-muted" style={{ fontSize: '0.74rem' }}>
                              {g.raca_animal ? `${g.raca_animal} · ` : ''}
                              {g.categoria_animal}
                              {g.idade_min_meses != null &&
                                ` · acima de ${g.idade_min_meses} meses`}
                            </span>
                          )}
                          {!isAnimal && g.area_plantada_ha != null && (
                            <span className="text-text-muted" style={{ fontSize: '0.74rem' }}>
                              {g.area_plantada_ha.toLocaleString('pt-BR')} ha plantados
                            </span>
                          )}
                        </div>
                      ) : g.imovel_denominacao ? (
                        <span className="font-medium">{g.imovel_denominacao}</span>
                      ) : (
                        <span>{g.descricao ?? '—'}</span>
                      )}
                    </td>
                    <td
                      style={{
                        fontSize: '0.82rem',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {g.imovel_matricula ? (
                        <Chip tone="info" size="sm">
                          {g.imovel_matricula}
                        </Chip>
                      ) : (
                        <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                          —
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        fontSize: '0.82rem',
                        textAlign: 'center',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {isAnimal ? (
                        `${g.quantidade_cabecas!.toLocaleString('pt-BR')} cab`
                      ) : isPenhor ? (
                        g.quantidade_sacas != null ? (
                          <div className="flex flex-col gap-0.5">
                            <span>{g.quantidade_sacas.toLocaleString('pt-BR')} sc</span>
                            {g.quantidade_kg != null && (
                              <span className="text-text-muted" style={{ fontSize: '0.74rem' }}>
                                {g.quantidade_kg.toLocaleString('pt-BR')} kg
                              </span>
                            )}
                          </div>
                        ) : g.quantidade_kg != null ? (
                          `${g.quantidade_kg.toLocaleString('pt-BR')} kg`
                        ) : (
                          '—'
                        )
                      ) : (
                        <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                          —
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        fontSize: '0.82rem',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {isAnimal && g.valor_unitario != null ? (
                        <span title="por cabeça">{formatBRL(g.valor_unitario)}/cab</span>
                      ) : isPenhor && g.valor_saca != null ? (
                        formatBRL(g.valor_saca)
                      ) : (
                        <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                          —
                        </span>
                      )}
                    </td>
                    <td
                      style={{
                        fontSize: '0.82rem',
                        textAlign: 'right',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {valorTotal != null ? formatBRL(valorTotal) : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <RequiredSectionEmpty>
            Pendente de cadastro: nenhuma garantia vinculada à operação.
          </RequiredSectionEmpty>
        )}
      </div>

      {/* Depósito em armazém — só quando há parcelamento */}
      {data.tipo_liquidacao === 'parcelada' && data.depositos && data.depositos.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionLabel>Depósito de grãos em armazém</SectionLabel>
          {data.depositos.map((d) => (
            <div
              key={d.id}
              className="flex flex-col gap-3 p-4"
              style={{
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
              }}
            >
              <div className="flex items-start gap-3">
                <span
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: 'rgba(16,185,129,.10)',
                    color: 'var(--ok)',
                  }}
                >
                  <Warehouse size={18} strokeWidth={1.5} />
                </span>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="font-medium" style={{ fontSize: '0.9rem' }}>
                    {d.armazem_nome ?? 'Armazém'}
                  </span>
                  <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                    {[
                      d.armazem_endereco,
                      d.armazem_municipio && `${d.armazem_municipio}/${d.armazem_uf ?? ''}`,
                      d.armazem_cep,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </div>
                {d.data_deposito && (
                  <Chip tone="ok" size="sm">
                    {formatDateBR(d.data_deposito)}
                  </Chip>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <Chip tone="info" size="sm">
                  {d.produto}
                </Chip>
                {d.safra && (
                  <Chip tone="neutral" size="sm">
                    {d.safra}
                  </Chip>
                )}
                {d.tipo_armazem && (
                  <Chip tone="neutral" size="sm">
                    {d.tipo_armazem}
                  </Chip>
                )}
                {d.quantidade_kg != null && (
                  <Chip tone="accent" size="sm">
                    {d.quantidade_kg.toLocaleString('pt-BR')} kg
                  </Chip>
                )}
                {d.quantidade_sacas != null && (
                  <Chip tone="accent" size="sm">
                    {d.quantidade_sacas.toLocaleString('pt-BR')} sc
                  </Chip>
                )}
                {d.valor_saca != null && (
                  <Chip tone="warn" size="sm">
                    {formatBRL(d.valor_saca)}/sc
                  </Chip>
                )}
              </div>
              {d.fiel_depositario && (
                <p className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                  <span className="font-medium">Fiel depositário:</span> {d.fiel_depositario}
                </p>
              )}
              {d.observacoes && (
                <p className="text-text-2 whitespace-pre-wrap" style={{ fontSize: '0.78rem' }}>
                  {d.observacoes}
                </p>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Anexos */}
      {data.anexos && data.anexos.length > 0 && (
        <div className="flex flex-col gap-3">
          <SectionLabel>Anexos</SectionLabel>
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Documento</th>
                <th style={{ textAlign: 'center' }}>Data</th>
                <th style={{ textAlign: 'right' }}>Tamanho</th>
                <th style={{ textAlign: 'right' }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {data.anexos.map((a) => (
                <tr key={a.id}>
                  <td>
                    <Chip tone={anexoTone(a.categoria)} size="sm">
                      {anexoCategoriaLabel(a.categoria)}
                    </Chip>
                  </td>
                  <td style={{ fontSize: '0.82rem' }}>
                    <div className="flex items-center gap-2">
                      <FileText size={14} strokeWidth={1.5} className="text-text-muted shrink-0" />
                      <span className="font-medium">{a.titulo}</span>
                    </div>
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                    {a.data_documento ? formatDateBR(a.data_documento) : '—'}
                  </td>
                  <td
                    style={{ fontSize: '0.78rem', textAlign: 'right' }}
                    className="text-text-muted"
                  >
                    {formatBytes(a.tamanho_bytes)}
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <a
                      href={`/api/v2/operacoes-credito/anexos/${a.id}/download`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-accent hover:underline"
                      style={{ fontSize: '0.82rem' }}
                    >
                      <Download size={13} strokeWidth={1.5} />
                      Abrir
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── componente principal ──────────────────────────────────────────────────────

interface Props {
  clienteId: string | number;
}

export function OperacoesCreditoTable({ clienteId }: Props) {
  const { data, isLoading, isError } = useOperacoesCredito(clienteId);
  const [selected, setSelected] = useState<Operacao | null>(null);

  const [filterSafra, setFilterSafra] = useState('');
  const [filterInstituicao, setFilterInstituicao] = useState('');
  const [filterModalidade, setFilterModalidade] = useState('');
  const [filterCultura, setFilterCultura] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [mostrarQuitadas, setMostrarQuitadas] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeCount =
    (filterSafra ? 1 : 0) +
    (filterInstituicao ? 1 : 0) +
    (filterModalidade ? 1 : 0) +
    (filterCultura ? 1 : 0) +
    (filterStatus ? 1 : 0);

  const filterWrapRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!filtersOpen) return undefined;
    function onDown(e: MouseEvent) {
      if (filterWrapRef.current && !filterWrapRef.current.contains(e.target as Node)) {
        setFiltersOpen(false);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setFiltersOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [filtersOpen]);

  function limparFiltros() {
    setFilterSafra('');
    setFilterInstituicao('');
    setFilterModalidade('');
    setFilterCultura('');
    setFilterStatus('');
    setMostrarQuitadas(false);
  }

  // Opções derivadas do que a carteira do cliente realmente tem — evita oferecer
  // filtro que não retorna nada.
  const { safras, instituicoes, modalidades, culturas, statuses } = useMemo(() => {
    const ops = data ?? [];
    const uniq = (vals: (string | null | undefined)[]) =>
      [...new Set(vals.filter((v): v is string => !!v))];
    return {
      safras: uniq(ops.map((o) => getSafra(o))).sort((a, b) => b.localeCompare(a)),
      instituicoes: uniq(ops.map((o) => o.instituicao_nome)).sort((a, b) =>
        a.localeCompare(b, 'pt-BR'),
      ),
      modalidades: uniq(ops.map((o) => o.modalidade)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
      culturas: uniq(ops.map((o) => o.cultura)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
      statuses: uniq(ops.map((o) => o.status)).sort((a, b) => a.localeCompare(b, 'pt-BR')),
    };
  }, [data]);

  const filtered = useMemo(() => {
    return (data ?? []).filter((o) => {
      if (filterSafra && getSafra(o) !== filterSafra) return false;
      if (filterInstituicao && o.instituicao_nome !== filterInstituicao) return false;
      if (filterModalidade && o.modalidade !== filterModalidade) return false;
      if (filterCultura && o.cultura !== filterCultura) return false;
      if (filterStatus && o.status !== filterStatus) return false;
      if (!filterStatus && !mostrarQuitadas && o.status === 'quitada') return false;
      return true;
    });
  }, [data, filterSafra, filterInstituicao, filterModalidade, filterCultura, filterStatus, mostrarQuitadas]);

  if (isLoading) {
    return <div className="module-loading">Carregando operações de crédito…</div>;
  }

  if (isError) {
    return (
      <EmptyBlock
        icon={<AlertCircle size={26} strokeWidth={1.5} />}
        title="Erro ao carregar operações"
      />
    );
  }

  if (!data || data.length === 0) {
    return (
      <EmptyBlock
        icon={<Landmark size={26} strokeWidth={1.5} />}
        title="Nenhuma operação cadastrada"
        desc="Cadastre cédulas de custeio, investimento ou comercialização."
      />
    );
  }

  // ── summary cards ────────────────────────────────────────────────────────────
  // Os stats acompanham o filtro — de outro modo o "Em aberto" contradiz a tabela.
  const emAberto = filtered.filter(vigente);
  const saldoVigentes = totalVigentes(filtered);
  const cronogramasEstimados = emAberto.filter((o) => o.observacoes?.includes('CRONOGRAMA ESTIMADO'));
  const cronogramasDocumentados = emAberto.filter((o) => !o.observacoes?.includes('CRONOGRAMA ESTIMADO'));
  const documentadas = resumoVincendas(cronogramasDocumentados);
  const estimadas = resumoVincendas(cronogramasEstimados);
  const { proxima: proximaParc } = resumoVincendas(emAberto);
  const maiorAvaliacao = Math.max(
    0,
    ...filtered.flatMap((o) => o.garantias?.map((g) => g.valor_avaliacao ?? 0) ?? []),
  );
  // Média dos juros das operações em aberto. taxa_juros_aa pode vir como fração
  // (0.12) ou já em pontos percentuais (12) — normaliza antes de somar.
  const taxasAbertas = emAberto
    .map((o) => o.taxa_juros_aa)
    .filter((t): t is number => t != null)
    .map((t) => (t < 1 ? t * 100 : t));
  const mediaJuros =
    taxasAbertas.length > 0 ? taxasAbertas.reduce((s, t) => s + t, 0) / taxasAbertas.length : null;
  // Total geral = soma das cédulas (operações) + soma dos prêmios de seguro.

  const totalSeguros = filtered.reduce((s, o) => s + (o.premio_seguros_total ?? 0), 0);
  return (
    <>
      <div className="stack" style={{ gap: '2rem' }}>
        {/* contagem + Filtrar por */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <Chip tone="neutral" size="sm">
            {filtered.length} {filtered.length === 1 ? 'operação' : 'operações'}
            {filtered.length !== data.length && ` de ${data.length}`}
          </Chip>

          {data.some((o) => o.status === 'quitada') && (
            <label className="flex items-center gap-2 text-sm text-text-2">
              <input
                type="checkbox"
                checked={mostrarQuitadas || filterStatus === 'quitada'}
                onChange={(e) => {
                  setMostrarQuitadas(e.target.checked);
                  if (filterStatus === 'quitada') setFilterStatus('');
                }}
              />
              Mostrar quitadas ({data.filter((o) => o.status === 'quitada').length})
            </label>
          )}
          <div ref={filterWrapRef} className="relative ml-auto">
            <button
              type="button"
              onClick={() => setFiltersOpen((v) => !v)}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 h-8 rounded-md text-[0.82rem]',
                'border border-border transition-colors',
                activeCount > 0 || filtersOpen
                  ? 'bg-[rgba(217,119,87,0.1)] text-accent border-transparent'
                  : 'bg-elevated text-text-2 hover:bg-surface2 hover:text-text',
              )}
            >
              <SlidersHorizontal size={13} strokeWidth={1.5} />
              Filtrar por
              {activeCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[15px] h-[15px] px-1 ml-1 rounded-full bg-accent text-[var(--on-solid)] text-[0.62rem] font-bold leading-none">
                  {activeCount}
                </span>
              )}
            </button>

            {filtersOpen && (
              <div
                className={cn(
                  'absolute top-[calc(100%+6px)] right-0 z-50',
                  'bg-surface border border-border rounded-xl shadow-[var(--shadow-over)]',
                  'p-4 min-w-[280px] flex flex-col gap-3',
                )}
              >
                {safras.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="opc-flt-safra"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Safra
                    </label>
                    <Select
                      id="opc-flt-safra"
                      value={filterSafra}
                      onChange={(e) => setFilterSafra(e.target.value)}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todas</option>
                      {safras.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {instituicoes.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="opc-flt-instituicao"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Instituição
                    </label>
                    <Select
                      id="opc-flt-instituicao"
                      value={filterInstituicao}
                      onChange={(e) => setFilterInstituicao(e.target.value)}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todas</option>
                      {instituicoes.map((i) => (
                        <option key={i} value={i}>
                          {i}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {modalidades.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="opc-flt-modalidade"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Modalidade
                    </label>
                    <Select
                      id="opc-flt-modalidade"
                      value={filterModalidade}
                      onChange={(e) => setFilterModalidade(e.target.value)}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todas</option>
                      {modalidades.map((m) => (
                        <option key={m} value={m}>
                          {modalidadeLabel(m)}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {culturas.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="opc-flt-cultura"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Cultura
                    </label>
                    <Select
                      id="opc-flt-cultura"
                      value={filterCultura}
                      onChange={(e) => setFilterCultura(e.target.value)}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todas</option>
                      {culturas.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                {statuses.length > 0 && (
                  <div className="flex flex-col gap-1">
                    <label
                      htmlFor="opc-flt-status"
                      className="text-[0.72rem] font-semibold uppercase tracking-wider text-text-2"
                    >
                      Status
                    </label>
                    <Select
                      id="opc-flt-status"
                      value={filterStatus}
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="text-[0.82rem]"
                    >
                      <option value="">Todos</option>
                      {statuses.map((s) => (
                        <option key={s} value={s}>
                          {s.charAt(0).toUpperCase() + s.slice(1)}
                        </option>
                      ))}
                    </Select>
                  </div>
                )}

                <button
                  type="button"
                  onClick={limparFiltros}
                  disabled={activeCount === 0}
                  className={cn(
                    'self-start text-[0.78rem] text-accent bg-transparent border-0 p-0',
                    'hover:underline disabled:opacity-50 disabled:cursor-not-allowed disabled:no-underline',
                  )}
                >
                  Limpar filtros
                </button>
              </div>
            )}
          </div>
        </div>

        <SaldosBancarios operacoes={filtered} />
        {/* stat bar */}
        <GridAuto min="sm" gap="md">
          <Stat
            label={saldoVigentes.bancario ? (saldoVigentes.incompleto ? 'Subtotal bancário informado' : 'Saldo bancário informado') : 'Saldo bancário'}
            value={
              saldoVigentes.bancario
                ? <span title="Posição bancária de referência; consulte a data-base nos detalhes.">{formatBRL(saldoVigentes.total)}</span>
                : 'A confirmar'
            }
            hint={saldoVigentes.incompleto ? 'Há operações sem saldo bancário confirmado. Parcelas não são tratadas como saldo bancário.' : undefined}
            icon={
              <IconBox size="lg" tone="solid">
                <Wallet size={19} strokeWidth={1.6} />
              </IconBox>
            }
          />

          <Stat
            label={documentadas.semCronograma && documentadas.comCronograma ? 'Subtotal das parcelas documentadas' : 'Parcelas documentadas a vencer'}
            value={documentadas.comCronograma === 0 ? 'A confirmar' : formatBRL(documentadas.total ?? documentadas.subtotal)}
            hint={`${documentadas.semCronograma ? `${documentadas.semCronograma} operação(ões) sem cronograma documentado. ` : ''}Não somar ao saldo bancário.`}
            icon={
              <IconBox size="lg" tone="solid">
                <Sprout size={19} strokeWidth={1.6} />
              </IconBox>
            }
          />

          {cronogramasEstimados.length > 0 && (
            <Stat
              label="Parcelas estimadas a vencer"
              value={estimadas.comCronograma === 0 ? 'A confirmar' : formatBRL(estimadas.total ?? estimadas.subtotal)}
              hint={`${estimadas.semCronograma ? `${estimadas.semCronograma} operação(ões) sem cronograma. ` : ''}Valores estimados; confirmar pela cédula ou extrato. Não somar ao saldo bancário.`}
              icon={
                <IconBox size="lg" tone="solid">
                  <CalendarDays size={19} strokeWidth={1.6} />
                </IconBox>
              }
            />
          )}

          {totalSeguros > 0 && (
            <Stat
              label="Seguros"
              value={<span title={formatBRL(totalSeguros)}>{formatBRLCompact(totalSeguros)}</span>}
              icon={
                <IconBox size="lg" tone="solid">
                  <ShieldCheck size={19} strokeWidth={1.6} />
                </IconBox>
              }
            />
          )}

          {mediaJuros != null && (
            <Stat
              label="Média de juros"
              value={formatTaxa(mediaJuros)}
              hint={`${taxasAbertas.length} operaç${taxasAbertas.length === 1 ? 'ão' : 'ões'} em aberto`}
              icon={
                <IconBox size="lg" tone="solid">
                  <Percent size={19} strokeWidth={1.6} />
                </IconBox>
              }
            />
          )}

          {proximaParc && (
            <Stat
              label="Próxima parcela"
              value={formatDateBR(proximaParc.data)}
              hint={
                <span title={formatBRL(proximaParc.valor)}>
                  {formatBRLCompact(proximaParc.valor)}
                </span>
              }
              icon={
                <IconBox size="lg" tone="solid">
                  <CalendarDays size={19} strokeWidth={1.6} />
                </IconBox>
              }
            />
          )}

          {maiorAvaliacao > 0 && (
            <Stat
              label="Maior garantia"
              value={
                <span title={formatBRL(maiorAvaliacao)}>{formatBRLCompact(maiorAvaliacao)}</span>
              }
              icon={
                <IconBox size="lg" tone="solid">
                  <CheckCircle2 size={19} strokeWidth={1.6} />
                </IconBox>
              }
            />
          )}

          <Stat
            label="Operações em aberto"
            value={String(emAberto.length)}
            icon={
              <IconBox size="lg" tone="solid">
                <Clock size={19} strokeWidth={1.6} />
              </IconBox>
            }
          />
        </GridAuto>

        {/* tabela */}
        {filtered.length === 0 ? (
          <EmptyBlock
            icon={<SlidersHorizontal size={26} strokeWidth={1.5} />}
            title="Nenhuma operação para esse filtro"
            desc="Ajuste ou limpe os filtros para ver as demais operações."
          />
        ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm w-full">
            <thead>
              <tr>
                <th>Cédula</th>
                <th style={{ textAlign: 'center' }}>Instituição</th>
                <th style={{ textAlign: 'center' }}>Cultura</th>
                <th style={{ textAlign: 'center' }}>Hectares</th>
                <th style={{ textAlign: 'center' }}>Modalidade</th>
                <th style={{ textAlign: 'center' }}>Taxa</th>
                <th style={{ textAlign: 'center' }}>Operação</th>
                <th style={{ textAlign: 'center' }}>Seguros</th>
                <th style={{ textAlign: 'center' }}>Em aberto</th>
                <th style={{ textAlign: 'center' }}>Vencimento</th>
                <th style={{ textAlign: 'center' }}>Próxima parcela</th>
                <th style={{ textAlign: 'center' }}>Valor da parcela</th>
                <th style={{ textAlign: 'center' }}>Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((op) => (
                <tr
                  key={op.id}
                  className="list-row-hover"
                  style={{ cursor: 'pointer' }}
                  onClick={() => setSelected(op)}
                >
                  <td style={{ fontSize: '0.83rem', fontVariantNumeric: 'tabular-nums' }}>
                    <span className="font-medium">{op.numero_cedula}</span>
                    <span
                      className="text-text-muted"
                      style={{ fontSize: '0.75rem', marginLeft: '0.4rem' }}
                      title={op.tipo_instrumento}
                    >
                      {shortTipoInstrumento(op.tipo_instrumento)}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="inline-flex">
                      <InstituicaoLogo nome={op.instituicao_nome} cor={op.instituicao_cor} />
                    </div>
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'center' }}>{op.cultura ?? op.subfinalidade ?? 'Não identificada'}</td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {op.area_ha != null ? `${op.area_ha.toLocaleString('pt-BR')} ha` : '—'}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Chip tone={modalidadeTone(op.modalidade)} size="sm">
                      {modalidadeLabel(op.modalidade)}
                    </Chip>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {op.taxa_juros_aa != null ? (
                      <Chip tone="info" size="sm">
                        {formatTaxa(op.taxa_juros_aa)}
                      </Chip>
                    ) : (
                      <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      fontSize: '0.83rem',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {formatBRL(op.valor_total)}
                  </td>
                  <td
                    style={{
                      fontSize: '0.83rem',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {op.premio_seguros_total != null && op.premio_seguros_total > 0 ? (
                      formatBRL(op.premio_seguros_total)
                    ) : (
                      <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      fontSize: '0.83rem',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {saldoExibido(op) != null ? (
                      <span className="font-semibold" style={{ color: 'var(--accent)' }}>
                        {formatBRL(saldoExibido(op)!)}
                      </span>
                    ) : (
                      <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                    {margem(op) ? <>Margem até {formatDateBR(op.data_vencimento)}<small className="block text-text-muted">Vencimento por ciclo/aditivo</small></> : formatDateBR(op.data_vencimento)}
                  </td>
                  <td style={{ fontSize: '0.82rem', textAlign: 'center' }}>
                    {op.proxima_parcela_data ? (
                      formatDateBR(op.proxima_parcela_data)
                    ) : (
                      <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td
                    style={{
                      fontSize: '0.82rem',
                      textAlign: 'center',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {op.proxima_parcela_valor != null ? (
                      formatBRL(op.proxima_parcela_valor)
                    ) : (
                      <span className="text-text-muted" style={{ fontSize: '0.78rem' }}>
                        —
                      </span>
                    )}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Chip tone={statusTone(op.status)} size="sm">
                      {op.status.charAt(0).toUpperCase() + op.status.slice(1)}
                    </Chip>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </div>

      <Modal
        open={selected != null}
        onOpenChange={(open) => {
          if (!open) setSelected(null);
        }}
        title={selected ? `Cédula ${selected.numero_cedula}` : ''}
        {...(selected && {
          description: `${selected.instituicao_nome ?? '—'} · ${modalidadeLabel(selected.modalidade)} · ${formatBRL(selected.valor_total)}`,
        })}
        {...(selected &&
          getSafra(selected) && {
            headerActions: (
              <Chip tone="ok" size="sm">
                {getSafra(selected)}
              </Chip>
            ),
          })}
        size="3xl"
      >
        {selected && <DetalheContent id={selected.id} />}
      </Modal>
    </>
  );
}
